import { DailyMetrics } from '../types';

// ─── Date helpers ─────────────────────────────────────────────────────────────

export function formatDate(d: string, opts?: { year?: 'numeric' | '2-digit' }) {
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (opts?.year) options.year = opts.year;
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', options);
}

/** Convert a Date to local YYYY-MM-DD string (avoids UTC bug from toISOString) */
export function localDateString(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Returns number of days since a date string, or null if date is yesterday or today */
export function dateStaleness(dateStr: string): number | null {
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  const yStr = localDateString(yest);
  if (dateStr >= yStr) return null;
  const todayStr = localDateString(now);
  return Math.round((new Date(todayStr).getTime() - new Date(dateStr + 'T12:00:00').getTime()) / 86400000);
}

export function getWeek(date = new Date()): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const weekNum = 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

export function weekToDate(w: string): Date {
  const [yearStr, weekStr] = w.split('-W');
  const year = parseInt(yearStr, 10);
  const week = parseInt(weekStr, 10);
  const jan4 = new Date(year, 0, 4);
  const week1Mon = new Date(jan4.getTime() - ((jan4.getDay() + 6) % 7) * 86400000);
  return new Date(week1Mon.getTime() + (week - 1) * 7 * 86400000);
}

// ─── Lifestyle pillar helpers ─────────────────────────────────────────────────

export function getPillarLabel(key: 'diet' | 'stress' | 'substances' | 'social', value: number | null): string {
  if (value == null) return '—';
  const maps: Record<string, string[]> = {
    diet:       ['Poor',         'Fair',    'Good',      'Excellent'],
    stress:     ['Overwhelmed',  'Struggling', 'Managing', 'Thriving'],
    substances: ['Problematic',  'Elevated', 'Moderate',  'None/Min'],
    social:     ['Isolated',     'Disconnected', 'Connected', 'Thriving'],
  };
  return maps[key]?.[value] ?? '—';
}

// Maps a 0-100 ring score to the standard 4-tier colour palette
export function ringColor(score: number | null | undefined): string {
  if (score == null || score === 0) return '#94A3B8'; // gray — no data
  if (score >= 85) return '#00A896'; // teal — excellent
  if (score >= 75) return '#22C55E'; // green — good
  if (score >= 60) return '#F59E0B'; // amber — fair
  return '#EF4444';                  // red — poor
}

export function getPillarColor(value: number | null, higherBetter: boolean): string {
  if (value == null) return '#94A3B8';
  const normalized = higherBetter ? value : (3 - value);
  if (normalized === 3) return '#00A896'; // teal — excellent
  if (normalized === 2) return '#22C55E'; // green — good
  if (normalized === 1) return '#F59E0B'; // amber — fair
  return '#EF4444';                       // red — poor
}

// ─── Key lab metadata: phenotype, optimal range, citation ─────────────────────
// "Optimal" = mental-health functional range, often tighter than standard labs.
// Three-tier color logic:  optimal (teal) → borderline (amber) → out of range (red)
export const KEY_LAB_META: Record<string, {
  phenotype: string;
  phenotypeColor: string;
  optLow?: number;
  optHigh?: number;
  citation: string;
  citationUrl: string;
}> = {
  'hsCRP':            { phenotype: 'Immune-Metabolic', phenotypeColor: '#EF4444', optLow: 0,    optHigh: 1.0,   citation: 'Mac Giollabhui et al., 2025 — Am J Psychiatry',        citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/41366844/' },
  'Insulin':          { phenotype: 'Immune-Metabolic', phenotypeColor: '#EF4444', optLow: 2,    optHigh: 8,     citation: 'Simmons et al., 2020 — Molecular Psychiatry',           citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/29899546/' },
  'Fasting Insulin':  { phenotype: 'Immune-Metabolic', phenotypeColor: '#EF4444', optLow: 2,    optHigh: 8,     citation: 'Simmons et al., 2020 — Molecular Psychiatry',           citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/29899546/' },
  'Cortisol':         { phenotype: 'HPA Axis',        phenotypeColor: '#F59E0B', optLow: 6,    optHigh: 18,    citation: 'Simmons et al., 2020 — Molecular Psychiatry',           citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/29899546/' },
  'Vitamin D':        { phenotype: 'Foundational',    phenotypeColor: '#64748B', optLow: 40,   optHigh: 60,    citation: 'Vitamin D meta-analysis, 2025 — Front Psychiatry',      citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/40821024/' },
  '25-OH Vitamin D':  { phenotype: 'Foundational',    phenotypeColor: '#64748B', optLow: 40,   optHigh: 60,    citation: 'Vitamin D meta-analysis, 2025 — Front Psychiatry',      citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/40821024/' },
  'Magnesium':        { phenotype: 'Foundational',    phenotypeColor: '#64748B', optLow: 2.07, optHigh: 2.4,   citation: 'Moabedi et al., 2023 — Front Psychiatry',              citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/38213402/' },
  'RBC Magnesium':    { phenotype: 'Foundational',    phenotypeColor: '#64748B', optLow: 5.5,  optHigh: 6.5,   citation: 'Moabedi et al., 2023 — Front Psychiatry',              citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/38213402/' },
  'Omega-3 Index':    { phenotype: 'Anti-inflammatory', phenotypeColor: '#00A896', optLow: 8,  optHigh: 12,    citation: 'Sublette et al., 2011 — J Clin Psychiatry',            citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/21939614/' },
  'Ferritin':         { phenotype: 'Fatigue / Mood',  phenotypeColor: '#8B5CF6', optLow: 50,   optHigh: 100,   citation: 'NHANES analysis, 2024 — Nutrients',                     citationUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10788288/' },
  'Vitamin B12':      { phenotype: 'Foundational',    phenotypeColor: '#64748B', optLow: 500,  optHigh: 1300,  citation: 'Neuropsychiatric optimal range — PubMed 35337631',       citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/35337631/' },
  'Folate':           { phenotype: 'Methylation',     phenotypeColor: '#6366F1', optLow: 15,   optHigh: 9999,  citation: 'VITACOG Trial, 2025 — Alzheimer\'s & Dementia',          citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/24965307/' },
  'RBC Folate':       { phenotype: 'Methylation',     phenotypeColor: '#6366F1', optLow: 500,  optHigh: 9999,  citation: 'VITACOG Trial, 2025 — Alzheimer\'s & Dementia',          citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/24965307/' },
  'Homocysteine':     { phenotype: 'Methylation',     phenotypeColor: '#6366F1', optLow: 0,    optHigh: 7,     citation: 'B-vitamin meta-analysis, 2014 — Am J Clin Nutr',        citationUrl: 'https://pubmed.ncbi.nlm.nih.gov/24965307/' },
  'TSH':              { phenotype: 'Thyroid',         phenotypeColor: '#0891B2', optLow: 1.0,  optHigh: 2.5,   citation: 'ATA Clinical Guidelines — subclinical threshold',        citationUrl: 'https://www.thyroid.org/hypothyroidism/' },
};

// Key markers — mental-health-specific, evidence-based
// Canonical marker names — must match normalizeMarker() output in labAliases.ts
export const KEY_MARKERS = [
  'hsCRP',
  'Insulin',
  'Cortisol',
  'Vitamin D',
  'Magnesium', 'RBC Magnesium',
  'Vitamin B12',
  'Omega-3 Index',
  'Ferritin',
  'Folate', 'RBC Folate',
  'Homocysteine',
  'TSH',
];

/**
 * Standard reference ranges for common labs.
 * Used as fallback when the lab record doesn't include ref_low/ref_high.
 * Keys are lowercase, alphanumeric-only (same as normalizeMarker output).
 */
export const LAB_REFERENCE_RANGES: Record<string, { low: number; high: number }> = {
  vitamind:      { low: 30, high: 100 },
  magnesium:     { low: 1.7, high: 2.4 },
  rbcmagnesium:  { low: 4.2, high: 6.8 },
  hscrp:         { low: 0, high: 3.0 },
  vitaminb12:    { low: 200, high: 1100 },
  folate:        { low: 3, high: 20 },
  rbcfolate:     { low: 280, high: 791 },
  omega3index:   { low: 4, high: 12 },
  ferritin:      { low: 15, high: 200 },
  homocysteine:  { low: 4, high: 15 },
  insulin:       { low: 2, high: 25 },
  hba1c:         { low: 4.0, high: 5.6 },
  glucose:       { low: 65, high: 99 },
  tsh:           { low: 0.4, high: 4.0 },
  freet4:        { low: 0.8, high: 1.8 },
  cortisol:      { low: 6, high: 23 },
  testosterone:  { low: 300, high: 1000 },
  totalcholesterol: { low: 100, high: 200 },
  ldl:           { low: 0, high: 100 },
  hdl:           { low: 40, high: 100 },
  triglycerides: { low: 0, high: 150 },
  hemoglobin:    { low: 13.5, high: 17.5 },
};

// ─── Mock data ────────────────────────────────────────────────────────────────
export function generateMockMetrics(days: number): DailyMetrics[] {
  const metrics: DailyMetrics[] = [];
  const now = new Date();
  for (let i = days; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const rand = (base: number, spread: number) =>
      Math.max(1, Math.min(100, Math.round(base + (Math.random() - 0.5) * spread)));
    metrics.push({
      date,
      sleep_score:      rand(74, 18),
      hrv:              rand(48, 20),
      readiness:        rand(72, 15),
      steps:            Math.round(7500 + (Math.random() - 0.5) * 5000),
      exercise_mins:    Math.round(25 + (Math.random() - 0.5) * 30),
      stress_score:     rand(35, 20),
      deep_sleep_mins:  Math.round(75 + (Math.random() - 0.5) * 40),
      rem_sleep_mins:   Math.round(90 + (Math.random() - 0.5) * 40),
      light_sleep_mins: Math.round(240 + (Math.random() - 0.5) * 60),
      total_sleep_mins: Math.round(420 + (Math.random() - 0.5) * 60),
      sleep_efficiency: rand(82, 15),
      lowest_hr:        Math.round(52 + (Math.random() - 0.5) * 10),
      bedtime_start:    null,
    });
  }
  return metrics;
}
